import React from 'react';
import styled from 'styled-components';
import PlusIcon from '../../../assets/icons/PlusIcon';
import EditIcon from '../../../assets/icons/EditIcon';
import StyledText from '../../../components/StyledComponents/StyledText';
import { useTheme } from '../../../middleware/ThemeContext';
import { CorporateTheme } from '../../../utils/themes';
import { Button } from '../../../components/form/Button';
import DropdownButton from '../../../components/StyledComponents/StyledDropdownButton';
import { hexToRgba } from '../../../utils/helper';
import { useGetOrganizationsQuery } from '../../../api/portalApiSlice';

type HeaderDropdownItem = {
  label: string;
  onClick: () => void;
  id?: string | number;
  icon?: React.ReactNode;
};

const HeaderContainer = styled.div`
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const TextGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
`;

const ActionGroup = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const IconButton = styled.button`
  width: 44px;
  height: 44px;
  border:none;
  background: white;
  border-radius: 22px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f9fafb;
    border-color: #d1d5db;
  }
`;

const PrimaryButton = styled.button`
  padding: 10px 24px;
  background-color: #1e3a8a;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #1e40af;
    transform: translateY(-1px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }
`;

interface HeaderProps {
  onAdd?: () => void;
  onEdit?: () => void;
  onClick?: () => void;
  title: string;
  subtitle: string;
  showAddEditButton?: boolean;
  btnTitle?: string;
  dropdownItems?: HeaderDropdownItem[];
  btnTextColor?: string;
  btnBackgroundColor?: string;
  btnHoverColor?: string;
  btnIcon?: React.ReactNode;
  menuItemHoverColor?: string;
  selectedItemBackgroundColor?: string;
  selectedItemBorderColor?: string;
  selectedItemTextColor?: string;
  selectedItemIconColor?: string;
  showId?: boolean;
}

export const Header: React.FC<HeaderProps> = ({ onAdd, onEdit, onClick, title, subtitle, showAddEditButton, btnTitle, dropdownItems, btnTextColor, btnBackgroundColor, btnHoverColor, btnIcon, menuItemHoverColor, selectedItemBackgroundColor, selectedItemBorderColor, selectedItemTextColor, selectedItemIconColor, showId }) => {
  const { theme } = useTheme();  
  const hasOrganizationOptions = (dropdownItems?.length ?? 0) > 0;
  const hasDropdownMode = dropdownItems !== undefined;
  return (
    <HeaderContainer>
      <TextGroup>
        <StyledText fontSize='20px' fontWeight={700} textColor={'var(--app-primary-color)'}>{title}</StyledText>
        <StyledText fontSize='15px' fontWeight={400} textColor={'var(--app-text-color)'}>{subtitle}</StyledText>
      </TextGroup>

      <ActionGroup>
        {showAddEditButton ?
          <>
            <IconButton onClick={onAdd} aria-label="Add new">
              <PlusIcon size={15} color='var(--app-secondary-color)' />
            </IconButton>
            <IconButton onClick={onEdit} aria-label="Edit">
              <EditIcon size={22} color='var(--app-secondary-color)' />
            </IconButton>
          </>
          : null}

        {hasDropdownMode ? (
          hasOrganizationOptions ? (
            <DropdownButton
              height='207px'
              width='200px'
              buttonLabel={btnTitle || 'Select'}
              items={dropdownItems!}
              align="right"
              removeborder
              menuFontWeight={500}
              fontSize="14px"
              fontWeight={600}
              buttonTextColor={btnTextColor}
              buttonBackgroundColor={btnBackgroundColor}
              buttonHoverColor={btnHoverColor}
              menuItemHoverColor={hexToRgba(theme.CorporateTheme.colors.blueberry, 0.05)}
              menuItemSelectedBackgroundColor={hexToRgba(theme.CorporateTheme.colors.blueberry, 0.05)}
              menuItemSelectedBorderColor={theme.CorporateTheme.colors.blueberry}
              menuItemSelectedTextColor={selectedItemTextColor}
              menuItemSelectedIconColor={selectedItemIconColor}
              showId={showId}
              buttonStyle={{ padding: '8px 15px', borderRadius:'10px' }}
            />
          ) : null
        ) : (
          <Button $variant="customButton" onClick={onClick} radius='10px' btnBg={'var(--app-primary-color)'} color={'var(--app-text-color)'}>
            {btnTitle}
          </Button>
        )}
      </ActionGroup>
    </HeaderContainer>
  );
};